import { useEstimations } from "@/hooks/useEstimation";
import { IssueRateChart } from "@/components/admin/IssueRateChart";
import { IssueRateTable } from "@/components/admin/IssueRateTable";
import { Header } from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import type { Estimation } from "@/interfaces/estimation.interface";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BarChart3 } from "lucide-react";

export const IssueRatePage = () => {
  const navigate = useNavigate();
  const { isLoading, getFilteredEstimations } = useEstimations();
  const [estimations, setEstimations] = useState<Estimation[]>([]);

  useEffect(() => {
    const fetchEstimations = async () => {
      const { data } = await getFilteredEstimations(0, 1000); // Fetch first 1000 records
      setEstimations(data);
    };
    fetchEstimations();
  }, [getFilteredEstimations]);

  const handleBackToAdmin = () => {
    navigate("/admin");
  };

  if (isLoading) {
    return (
      <div className="relative flex h-full w-full flex-col">
        <Header
          leftSection={
            <div className="flex flex-row items-center space-x-2">
              <BarChart3 className="h-5 w-5" />
              <p>Issue Rate Analytics</p>
            </div>
          }
          rightSection={
            <Button onClick={handleBackToAdmin} className="!rounded-lg !bg-[#000] !px-4 !py-2 !text-sm leading-6 !font-medium text-white">
              <ArrowLeft className="h-4 w-4" />
              <p>Back to Admin</p>
            </Button>
          }
        />
        <div className="p-6">
          <div className="animate-pulse">
            <div className="mb-4 h-8 w-1/4 rounded bg-gray-200"></div>
            <div className="h-[400px] rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full w-full flex-col">
      <Header
        leftSection={
          <div className="flex flex-row items-center space-x-2">
            <BarChart3 className="h-5 w-5" />
            <p>Issue Rate Analytics</p>
          </div>
        }
        rightSection={
          <Button onClick={handleBackToAdmin} className="!rounded-lg !bg-[#000] !px-4 !py-2 !text-sm leading-6 !font-medium text-white">
            <ArrowLeft className="h-4 w-4" />
            <p>Back to Admin</p>
          </Button>
        }
      />
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          <div className="rounded-lg bg-white p-6 shadow">
            <h2 className="mb-4 text-xl font-semibold">Issue Rate Analytics</h2>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg bg-white p-6 shadow">
                <h3 className="mb-2 text-center text-lg font-semibold">Avg Issue Rate (All)</h3>
                <p className="text-center text-2xl font-bold">
                  {Math.round(
                    estimations
                      .filter((estimation) => estimation.analytics) // Only include estimations with analytics
                      .reduce((acc, estimation) => acc + (estimation.analytics?.issueRate ?? 0), 0) /
                      estimations.filter((estimation) => estimation.analytics).length,
                  ) || 0}
                  %
                </p>
              </div>
              <div className="rounded-lg bg-white p-6 shadow">
                <h3 className="mb-2 text-center text-lg font-semibold">Avg Issue Rate (Post)</h3>
                <p className="text-center text-2xl font-bold">
                  {Math.round(
                    estimations
                      .filter((estimation) => estimation.analyticsPostProcessing && estimation.analytics) // Only include estimations with analytics
                      .reduce(
                        (acc, estimation) =>
                          acc +
                          ((estimation.analyticsPostProcessing?.updatedIssueCount ?? 0) /
                            (estimation.analytics?.totalNumberOfLineItems ?? 0)) *
                            100,
                        0,
                      ) / estimations.filter((estimation) => estimation.analyticsPostProcessing && estimation.analytics).length,
                  ) || 0}
                  %
                </p>
              </div>
              <div className="rounded-lg bg-white p-6 shadow">
                <h3 className="mb-2 text-center text-lg font-semibold">Avg Processing Time</h3>
                <p className="text-center text-2xl font-bold">
                  {(() => {
                    const estimationsWithProcessingTime = estimations.filter(
                      (estimation) => estimation.analyticsPostProcessing?.processingTimeMs,
                    );
                    if (estimationsWithProcessingTime.length === 0) return "0s";

                    const avgMs =
                      estimationsWithProcessingTime.reduce(
                        (acc, estimation) => acc + (estimation.analyticsPostProcessing?.processingTimeMs ?? 0),
                        0,
                      ) / estimationsWithProcessingTime.length;

                    const avgSeconds = avgMs / 1000;
                    return avgSeconds < 1 ? `${Math.round(avgMs)}ms` : `${avgSeconds.toFixed(1)}s`;
                  })()}
                </p>
              </div>
            </div>
            <IssueRateChart estimations={estimations} />
            <div className="mt-4 w-full">
              <h2 className="mb-4 text-xl font-semibold">Issue Rate Table</h2>
              <IssueRateTable estimations={estimations} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
